import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { Button } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import Swal from "sweetalert2";
import authInstance from "src/api/apiConfig";
import { successAlert } from "src/utils/Swal";
import {
  COMPANY_AddLogo,
  COMPANY_Address,
  COMPANY_NAME,
} from "src/services/const";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const Banking = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const SchemaName = sessionStorage.getItem("schemaName");
  const [emiStatus, setEmiStatus] = useState("");
  const [openModel, setOpenModel] = useState(false);
  const [printState, setPrintState] = useState(false);
  const [commonData, setCommonData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(200); // <-- dynamic rows per page
  const BRANCH_ID = sessionStorage.getItem("branc");
  const [allSelectedData, setAllSelectedData] = useState(null);

  const debouncedSearch = useDebounce(searchInput, 200);

  const componentPDF = useRef();

  // Fetch data only on page change or schemaName change
  const getAllcommonData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authInstance.post("getallcommondata", {
        schemaName: SchemaName,
        page: page,
      });
      if (data.status === 200) {
        const { newCapital, newData, newJournal, newPayment, totals } =
          data?.data?.message;
        setTotal(totals);
        const combinedData = [
          ...newCapital,
          ...newData,
          ...newJournal,
          ...newPayment,
        ];
        if (combinedData.length === 0) {
          setHasMore(false);
        } else {
          setCommonData((prevData) => [...prevData, ...combinedData]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [SchemaName, page]);

  useEffect(() => {
    getAllcommonData();
  }, [getAllcommonData]);

  const generatePDF = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "userData",
    onAfterPrint: () => setPrintState(false),
  });

  useEffect(() => {
    if (printState) {
      generatePDF();
    }
  }, [printState, generatePDF]);

  const handleSubmitEditForm = async () => {
    if (!allSelectedData) return;
    const payload = {
      status: emiStatus,
      schemaName: SchemaName,
    };
    try {
      const response = await authInstance.patch(
        `payment/${allSelectedData.id}`,
        payload
      );
      if (response.status === 201) {
        setOpenModel(false);
        successAlert("Date successfully updated");
        setCommonData([]); // Reset data to refetch fresh
        setPage(1);
        setHasMore(true);
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to update payment", "error");
    }
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // reset page to 1 on rows per page change
  };

  const getNormalizedDate = (item) => {
    return new Date(
      item?.paymentDate ||
      item?.DisbursementDate ||
      item?.entryDate ||
      "1970-01-01"
    );
  };

  // Filter + sort with memo & debounced input
  const filteredSortedData = useMemo(() => {
    if (!Array.isArray(commonData)) return [];

    const lowerSearch = debouncedSearch.toLowerCase();

    const filtered = commonData.filter((payment) => {
      const aadharNumber =
        payment?.customer?.aadharNumber?.toString().toLowerCase() || "";
      const mobile =
        payment?.customer?.phoneNumber?.toString().toLowerCase() || "";
      const loanAccountNo =
        payment?.customer?.loanAccountNumber?.toLowerCase() || "";
      const branchCode = payment?.branch?.branchCode?.toLowerCase() || "";
      const branchName = payment?.branch?.branchName?.toLowerCase() || "";
      const firstName = payment?.customer?.firstName?.toLowerCase() || "";
      const directorName = payment?.director?.directorName?.toLowerCase() || "";
      const debitAccount = payment?.debitAccount?.toLowerCase() || "";
      const creditAccount = payment?.creditAccount?.toLowerCase() || "";

      // if (!lowerSearch) return true;

      if (!isNaN(lowerSearch)) {
        return (
          aadharNumber.includes(lowerSearch) || mobile.includes(lowerSearch)
        );
      }

      return (
        loanAccountNo.includes(lowerSearch) ||
        branchName.includes(lowerSearch) ||
        branchCode.includes(lowerSearch) ||
        firstName.includes(lowerSearch) ||
        directorName.includes(lowerSearch) ||
        debitAccount.includes(lowerSearch) ||
        creditAccount.includes(lowerSearch)
      );
    });

    return filtered.sort((a, b) => getNormalizedDate(b) - getNormalizedDate(a));
  }, [commonData, debouncedSearch]);

  // Paginate displayed data after filtering
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const displayedData = filteredSortedData.slice(
    indexOfFirstRow,
    indexOfLastRow
  );

  const handleNextPage = () => {
    if (indexOfLastRow < filteredSortedData.length) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const download_btn = () => {
    setPrintState(true);
  };

  return (
    <div className="content">
      <CModal
        scrollable
        visible={openModel}
        onClose={() => setOpenModel(false)}
        aria-labelledby="ScrollingLongContentExampleLabel2"
      >
        <CModalHeader>
          <CModalTitle id="ScrollingLongContentExampleLabel2">{`${allSelectedData?.customer?.firstName}`}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CRow>
            <CCol xs={10}>
              <CFormInput
                type={"text"}
                value={emiStatus}
                placeholder={`${allSelectedData?.status}`}
                onChange={(e) => setEmiStatus(e.target.value)}
              />
            </CCol>
          </CRow>
        </CModalBody>
        <CModalFooter>
          <CButton onClick={handleSubmitEditForm} color="primary">
            Payment
          </CButton>
        </CModalFooter>
      </CModal>

      <CCard className="mb-4 custom-card">
        <CCardHeader className="custom-card-header">
          <h5>All Banking Record</h5>
        </CCardHeader>
        <CCardBody>
          <div>
            <div className="searchinput">
              <CRow>
                <CCol xs={12} md={4} lg={4}>
                  <CFormLabel htmlFor="groupName" className="custom-form-label">
                    Enter Value for Search
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    size="sm"
                    placeholder="Enter desired value for searching"
                    aria-label="sm input example"
                    style={{ margin: "0px 0px 16px 0px" }}
                    onChange={handleSearchChange}
                    value={searchInput}
                    className="custom-form-input"
                  />
                </CCol>

                {/* Rows per page selector */}
                <CCol xs={12} md={3} lg={2}>
                  <CFormLabel
                    htmlFor="rowsPerPage"
                    className="custom-form-label"
                    style={{ marginTop: "32px" }}
                  >
                    Rows Per Page
                  </CFormLabel>
                  <CFormSelect
                    id="rowsPerPage"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="sm"
                    style={{ marginBottom: "16px" }}
                  >
                    {[100, 200, 300, 400, 500].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>

                <CCol xs={12} md={3} lg={2}>
                  <Button
                    className="add-btn mr-10 w-auto"
                    onClick={download_btn}
                    style={{ marginTop: "33px" }}
                  >
                    Download
                  </Button>
                </CCol>
              </CRow>
            </div>
            <div
              className="new-common-table-wrapper mb-2 new-table-container"
              ref={printState ? componentPDF : null}
              style={{ padding: printState ? "20px" : "0px" }}
            >
              {printState && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    background:
                      "linear-gradient(180deg, #1e2639, #222b40, #4c5a7a, #7c90c2)",
                    padding: "20px",
                  }}
                >
                  <img
                    src={COMPANY_AddLogo}
                    alt="Logo"
                    width="50px"
                    height="50px"
                  />
                  <p style={{ color: "#FFF" }}>Banking Details</p>
                  <div style={{ textAlign: "start" }}>
                    <p
                      style={{
                        fontWeight: "bold",
                        color: "#FFF",
                        fontSize: "20px",
                      }}
                    >
                      {COMPANY_NAME}
                    </p>
                    <p style={{ color: "#FFF" }}>{`(${COMPANY_Address})`}</p>
                  </div>
                </div>
              )}
              <CRow>
                <CCol xs={6}>
                  <div>
                    <span>
                      Total Payment Amount :{" "}
                      {total?.totalPaymentAmount + total?.bankTotal}
                    </span>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div>
                    <span>
                      Total Disbursement Amount :{" "}
                      {total?.totalDisbursementAmount}
                    </span>
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={6}>
                  <div>
                    <span>
                      Total Debit Amount :{" "}
                      {total?.totalDebitAmount - total?.bankTotal}
                    </span>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <div>
                    <span>
                      Total Loan Amount :{" "}
                      {total?.totalLoanAmount + total?.totalCapitalAmount}
                    </span>
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={6}>
                  <div>
                    <span>
                      Total Amount :{" "}
                      <span style={{ color: "green" }}>
                        {total?.totalPaymentAmount +
                          total?.totalCapitalAmount +
                          total?.bankTotal * 2 +
                          total?.totalLoanAmount -
                          total?.totalDisbursementAmount -
                          total?.totalDebitAmount}
                      </span>
                    </span>
                  </div>
                </CCol>
              </CRow>

              <CTable hover className="custom-tablee " responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell scope="col">
                      <span>Sr. No.</span>
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      <span>Date</span>
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      <span>Name</span>
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      <span>Payment</span>
                    </CTableHeaderCell>
                    <CTableHeaderCell scope="col">
                      <span>Received</span>
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {displayedData.map((company, index) => (
                    <CTableRow key={company?.id || index}>
                      <CTableDataCell>
                        {indexOfFirstRow + index + 1}
                      </CTableDataCell>
                      <CTableDataCell>
                        {new Date(
                          company?.paymentDate ||
                          company?.DisbursementDate ||
                          company?.entryDate ||
                          "--"
                        ).toLocaleDateString()}
                      </CTableDataCell>
                      <CTableDataCell>
                        {company?.customer?.firstName ||
                          company?.director?.directorName ||
                          company?.debitAccount ||
                          "--"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {company?.debitAmount ||
                          company?.DisbursementAmount ||
                          "--"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {company?.paymentAmount ||
                          company?.loanAmount ||
                          (company?.debitAccount === "Bank" &&
                            company?.debitAmount) ||
                          "--"}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              {loading && <div>Loading...</div>}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "10px",
              }}
            >
              <CButton onClick={handlePrevPage} disabled={currentPage === 1}>
                Prev
              </CButton>
              <div>Page {currentPage}</div>
              <CButton
                onClick={handleNextPage}
                disabled={
                  loading ||
                  !hasMore ||
                  indexOfLastRow >= filteredSortedData.length
                }
              >
                Next
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default Banking;
